import { ErrorMessage } from '@wyz/ui';
import { isNil } from 'lodash-es';
import { Trans } from '~/common/context';
import { useManageSearch } from '~/purchase/api';
import {
  QuoteColumnarDisplay,
  QuoteLinesDisplay,
  QuoteStandardDisplay,
} from '~/purchase/components';
import { IQuoteSetting, IQuoteTyreOffer } from '~/purchase/types';

import { memo, useCallback } from 'react';
import { IQuoteUiState, IUserCustomer } from '~/common/types';
import { IProductListState, ISearchTypeArgs } from '~/purchase/types';
import { IBaseTyreOffer } from '@wyz/types';
import { QUOTE_DISPLAY_TYPE } from '~/common/schemas';

type Props = {
  offers: IQuoteTyreOffer[];
  quoteSetting: IQuoteSetting;
  customer: IUserCustomer;
  uiState: IQuoteUiState;
  searchType: ISearchTypeArgs['searchType'];
  searchQuery: IProductListState['searchQuery'];
  onUpdateQuery: (tyreOffer: IBaseTyreOffer) => IBaseTyreOffer | undefined;
};
const Offers = ({
  offers,
  customer,
  quoteSetting,
  uiState,
  searchType,
  searchQuery,
  onUpdateQuery,
}: Props) => {
  const manageSearch = useManageSearch<IQuoteTyreOffer>({
    context: {
      customerId: customer.id,
      searchType,
    },
    refreshEan: { enabled: true },
  });

  const handleOnRefreshTyre = useCallback(
    (ean: string, newQuantity: number) => {
      if (isNil(searchQuery)) {
        return;
      }
      manageSearch.refreshEan.mutate(
        {
          ean: ean,
          baseQuery: {
            ...searchQuery,
            quantity: newQuantity,
          },
          forceNoLatency: false,
        },
        {
          onSuccess: (tyreOffer) => {
            if (isNil(tyreOffer) || isNil(searchQuery)) {
              return;
            }
            onUpdateQuery(tyreOffer);
          },
        },
      );
    },
    [manageSearch.refreshEan, onUpdateQuery, searchQuery],
  );

  return (
    <>
      {offers.length === 0 ? (
        <ErrorMessage
          title={<Trans i18nKey='app.purchase.search_page.no_results' />}
        />
      ) : (
        <>
          {(uiState.display === QUOTE_DISPLAY_TYPE.STANDARD ||
            uiState.display === QUOTE_DISPLAY_TYPE.UNSET) && (
            <QuoteStandardDisplay
              items={offers}
              customer={customer}
              quoteSetting={quoteSetting}
              requestedQuantity={searchQuery?.quantity ?? 2}
              showBuyingInfo={uiState.showBuyingInfo}
              onRefreshTyre={handleOnRefreshTyre}
            />
          )}
          {uiState.display === QUOTE_DISPLAY_TYPE.COLUMNAR && (
            <QuoteColumnarDisplay
              items={offers}
              customer={customer}
              quoteSetting={quoteSetting}
              requestedQuantity={searchQuery?.quantity ?? 2}
              showBuyingInfo={uiState.showBuyingInfo}
              onRefreshTyre={handleOnRefreshTyre}
            />
          )}
          {uiState.display === QUOTE_DISPLAY_TYPE.LINES && (
            <QuoteLinesDisplay
              items={offers}
              customer={customer}
              quoteSetting={quoteSetting}
              requestedQuantity={searchQuery?.quantity ?? 2}
              showBuyingInfo={uiState.showBuyingInfo}
              onRefreshTyre={handleOnRefreshTyre}
            />
          )}
        </>
      )}
    </>
  );
};

export default memo(Offers);
